
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Building, User, Users } from 'lucide-react';

const createCustomerFormSchema = (isCustomerNumberRequired: boolean) => {
  return z.object({
    customer_name: z.string().min(1, { message: "Customer name is required" }),
    customer_number: isCustomerNumberRequired 
      ? z.string().min(1, { message: "Customer number is required" })
      : z.string().optional(),
    customer_type: z.enum(["private", "company", "reseller"], {
      required_error: "Please select customer type"
    }),
    phone: z.string().optional(),
    email: z.string().email({ message: "Invalid email address" }).optional().or(z.literal('')),
    organisation_number: z.string().optional(),
    contact_firstname: z.string().optional(),
    contact_lastname: z.string().optional(),
    postal_street: z.string().optional(),
    postal_zip_code: z.string().optional(),
    postal_city: z.string().optional(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
  });
};

export type CustomerFormValues = z.infer<ReturnType<typeof createCustomerFormSchema>>;

interface CustomerFormProps {
  onSubmit: (data: CustomerFormValues) => void;
  isLoading?: boolean;
  submitButtonText?: string;
  id?: string;
  hideCustomerNumber?: boolean;
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  onSubmit,
  isLoading = false,
  submitButtonText = "Create Customer",
  id,
  hideCustomerNumber = false
}) => {
  const [customerType, setCustomerType] = useState<"private" | "company" | "reseller">("private");

  const customerFormSchema = createCustomerFormSchema(!hideCustomerNumber);

  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      customer_name: '',
      customer_number: '',
      customer_type: 'private',
      phone: '',
      email: '',
      organisation_number: '',
      contact_firstname: '',
      contact_lastname: '',
      postal_street: '',
      postal_zip_code: '',
      postal_city: '',
      first_name: '',
      last_name: '',
    }
  });

  const handleTypeChange = (value: string) => {
    setCustomerType(value as "private" | "company" | "reseller");
    form.setValue("customer_type", value as "private" | "company" | "reseller");
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" id={id}>
        <FormField
          control={form.control}
          name="customer_type"
          render={({ field }) => (
            <FormItem className="space-y-1">
              <FormLabel>Customer Type</FormLabel>
              <RadioGroup 
                onValueChange={(value) => handleTypeChange(value)}
                defaultValue={field.value}
                className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="private" id="private-customer" />
                  <label htmlFor="private-customer" className="flex items-center cursor-pointer">
                    <User className="h-4 w-4 mr-2" />
                    <span>Private</span>
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="company" id="company-customer" />
                  <label htmlFor="company-customer" className="flex items-center cursor-pointer">
                    <Building className="h-4 w-4 mr-2" />
                    <span>Company</span>
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="reseller" id="reseller-customer" />
                  <label htmlFor="reseller-customer" className="flex items-center cursor-pointer">
                    <Users className="h-4 w-4 mr-2" />
                    <span>Reseller</span>
                  </label>
                </div>
              </RadioGroup>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="customer_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {customerType === "private" ? "Full Name" : "Company Name"}
              </FormLabel>
              <FormControl>
                <Input placeholder={customerType === "private" ? "Full name" : "Company name"} disabled={isLoading} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {!hideCustomerNumber && (
          <FormField
            control={form.control}
            name="customer_number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Customer Number</FormLabel>
                <FormControl>
                  <Input placeholder="Customer number" disabled={isLoading} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Fields for private customers */}
        {customerType === "private" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="First name" disabled={isLoading} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Last name" disabled={isLoading} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input placeholder="Phone number" disabled={isLoading} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="Email address" type="email" disabled={isLoading} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            {/* Postal address fields for private customers */}
            <div className="space-y-4 border p-4 rounded-md">
              <h3 className="font-medium">Postal Address</h3>
              <FormField
                control={form.control}
                name="postal_street"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Street</FormLabel>
                    <FormControl>
                      <Input placeholder="Street address" disabled={isLoading} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="postal_zip_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Zip Code</FormLabel>
                      <FormControl>
                        <Input placeholder="Zip code" disabled={isLoading} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="postal_city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City</FormLabel>
                      <FormControl>
                        <Input placeholder="City" disabled={isLoading} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </>
        )}

        {/* Fields for company or reseller */}
        {(customerType === "company" || customerType === "reseller") && (
          <>
            <FormField
              control={form.control}
              name="organisation_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Organisation Number</FormLabel>
                  <FormControl>
                    <Input placeholder="Organisation number" disabled={isLoading} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="First name" disabled={isLoading} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Last name" disabled={isLoading} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact Phone</FormLabel>
                    <FormControl>
                      <Input placeholder="Phone number" disabled={isLoading} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact Email</FormLabel>
                    <FormControl>
                      <Input placeholder="Email address" type="email" disabled={isLoading} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            {/* Postal address fields for company/reseller */}
            <div className="space-y-4 border p-4 rounded-md">
              <h3 className="font-medium">Postal Address</h3>
              <FormField
                control={form.control}
                name="postal_street"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Street</FormLabel>
                    <FormControl>
                      <Input placeholder="Street address" disabled={isLoading} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="postal_zip_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Zip Code</FormLabel>
                      <FormControl>
                        <Input placeholder="Zip code" disabled={isLoading} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="postal_city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City</FormLabel>
                      <FormControl>
                        <Input placeholder="City" disabled={isLoading} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </>
        )}
        
        {submitButtonText && (
          <div className="flex justify-end">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Creating...' : submitButtonText}
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
};

export default CustomerForm;
